import React, { useEffect, useState } from 'react';
import { apiHeaders } from '../api';

const API_BASE = 'https://bot.nt55.ru';

interface Broadcast {
  id: number;
  name: string;
  template_text: string;
  attachments: any;
  audience_filter: any;
  status: string;
}

interface ButtonData {
  text: string;
  type: 'callback' | 'link';
  payload?: string;
  url?: string;
}

const Broadcasts: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [name, setName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [buttons, setButtons] = useState<ButtonData[]>([]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [viewLogsId, setViewLogsId] = useState<number | null>(null);

  const resetForm = () => {
    setName('');
    setTemplateText('');
    setImageUrl('');
    setButtons([]);
    setEditingId(null);
  };

  const load = async () => {
    const res = await fetch(`${API_BASE}/api/broadcasts/`, { headers: apiHeaders() });
    const data = await res.json();
    setBroadcasts(data);
  };

  useEffect(() => { load(); }, []);

  const buildAttachments = () => {
    const attachments: any[] = [];
    if (imageUrl.trim()) {
      attachments.push({ type: 'image', payload: { url: imageUrl.trim() } });
    }
    if (buttons.length > 0) {
      const rows = buttons
        .filter((btn) => btn.text.trim() !== '')
        .map((btn) => {
          if (btn.type === 'link') {
            return [{ type: 'link', text: btn.text, url: btn.url || '' }];
          } else {
            return [{ type: 'callback', text: btn.text, payload: btn.payload || '' }];
          }
        });
      if (rows.length > 0) {
        attachments.push({ type: 'inline_keyboard', payload: { buttons: rows } });
      }
    }
    return attachments;
  };

  const loadBroadcastToForm = (b: Broadcast) => {
    setName(b.name);
    setTemplateText(b.template_text);
    const attachments = b.attachments || [];
    const img = attachments.find((a: any) => a.type === 'image');
    const keyboard = attachments.find((a: any) => a.type === 'inline_keyboard');
    setImageUrl(img?.payload?.url || '');
    if (keyboard?.payload?.buttons) {
      const btns: ButtonData[] = keyboard.payload.buttons.map((row: any[]) => {
        const btn = row[0];
        return {
          text: btn.text || '',
          type: btn.type === 'link' ? 'link' : 'callback',
          payload: btn.payload || '',
          url: btn.url || '',
        };
      });
      setButtons(btns);
    } else {
      setButtons([]);
    }
  };

  const handleCreateOrUpdate = async () => {
    const attachments = buildAttachments();
    const payload = {
      name,
      template_text: templateText,
      attachments: attachments.length > 0 ? attachments : null,
    };
    const url = editingId
      ? `${API_BASE}/api/broadcasts/${editingId}`
      : `${API_BASE}/api/broadcasts/`;
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...apiHeaders() },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      resetForm();
      load();
    } else {
      alert('Ошибка сохранения');
    }
  };

  const handleEdit = (b: Broadcast) => {
    setEditingId(b.id);
    loadBroadcastToForm(b);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Удалить рассылку?')) {
      await fetch(`${API_BASE}/api/broadcasts/${id}`, { method: 'DELETE', headers: apiHeaders() });
      load();
    }
  };

  const handleStart = async (id: number) => {
    const res = await fetch(`${API_BASE}/api/broadcasts/${id}/start`, { method: 'POST', headers: apiHeaders() });
    if (res.ok) {
      alert('Рассылка запущена');
      load();
    } else {
      const err = await res.json();
      alert(err.detail || 'Ошибка запуска');
    }
  };

  const handleViewLogs = async (id: number) => {
    const res = await fetch(`${API_BASE}/api/broadcasts/${id}/logs`, { headers: apiHeaders() });
    const data = await res.json();
    setLogs(data);
    setViewLogsId(id);
  };

  const addButton = () => setButtons([...buttons, { text: '', type: 'callback', payload: '' }]);
  const updateButton = (index: number, field: keyof ButtonData, value: string) => {
    const updated = [...buttons];
    (updated[index] as any)[field] = value;
    setButtons(updated);
  };
  const removeButton = (index: number) => setButtons(buttons.filter((_, i) => i !== index));

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: 32, fontFamily: '"Inter", "Roboto", sans-serif' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Рассылки</h2>

        <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 32, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
          <h3 style={{ margin: '0 0 16px', fontWeight: 600 }}>{editingId ? 'Редактировать' : 'Новая рассылка'}</h3>
          <input placeholder="Название" value={name} onChange={e => setName(e.target.value)}
            style={{ width: '100%', marginBottom: 12, padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }} />
          <textarea placeholder="Текст сообщения" value={templateText} onChange={e => setTemplateText(e.target.value)}
            style={{ width: '100%', minHeight: 80, marginBottom: 12, padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }} />

          <label style={{ fontWeight: 500 }}>URL картинки</label>
          <input placeholder="https://..." value={imageUrl} onChange={e => setImageUrl(e.target.value)}
            style={{ width: '100%', marginTop: 4, marginBottom: 16, padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9' }} />

          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>Кнопки ({buttons.length})</summary>
            <div style={{ marginTop: 12 }}>
              {buttons.map((btn, idx) => (
                <div key={idx} style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 8, border: '1px solid #f0f0f0' }}>
                  <input placeholder="Текст" value={btn.text} onChange={e => updateButton(idx, 'text', e.target.value)}
                    style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9' }} />
                  <select value={btn.type} onChange={e => updateButton(idx, 'type', e.target.value)}
                    style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
                    <option value="callback">Callback</option>
                    <option value="link">Ссылка</option>
                  </select>
                  {btn.type === 'callback' ? (
                    <input placeholder="Payload" value={btn.payload || ''} onChange={e => updateButton(idx, 'payload', e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9' }} />
                  ) : (
                    <input placeholder="URL" value={btn.url || ''} onChange={e => updateButton(idx, 'url', e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9' }} />
                  )}
                  <button onClick={() => removeButton(idx)} style={{ marginTop: 8, background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>Удалить</button>
                </div>
              ))}
              <button onClick={addButton} style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '6px 16px', cursor: 'pointer' }}>+ Добавить кнопку</button>
            </div>
          </details>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreateOrUpdate} style={{ background: '#1677ff', color: 'white', border: 'none', padding: '8px 20px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
              {editingId ? 'Сохранить' : 'Создать'}
            </button>
            {editingId && <button onClick={resetForm} style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 20px', cursor: 'pointer' }}>Отмена</button>}
          </div>
        </div>

        <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Список рассылок</h3>
        {broadcasts.length === 0 ? (
          <div style={{ color: '#888', textAlign: 'center', padding: 40 }}>Нет созданных рассылок</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {broadcasts.map(b => (
              <div key={b.id} style={{ background: 'white', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                <div>
                  <strong style={{ fontSize: 16 }}>{b.name}</strong>
                  <span style={{ marginLeft: 12, fontSize: 12, padding: '2px 8px', borderRadius: 12, background: b.status === 'draft' ? '#e6f7ff' : b.status === 'sending' ? '#fff7e6' : b.status === 'done' ? '#f6ffed' : '#f5f5f5', color: b.status === 'draft' ? '#096dd9' : b.status === 'sending' ? '#d46b08' : b.status === 'done' ? '#389e0d' : '#888', fontWeight: 500 }}>
                    {b.status === 'draft' ? 'Черновик' : b.status === 'sending' ? 'Отправляется' : b.status === 'done' ? 'Завершена' : b.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(b.status === 'draft' || b.status === 'failed') && (
                    <>
                      <button onClick={() => handleStart(b.id)} style={{ background: '#1677ff', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>▶️ Запустить</button>
                      <button onClick={() => handleEdit(b)} style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>✏️</button>
                      <button onClick={() => handleDelete(b.id)} style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: '#cf1322' }}>🗑️</button>
                    </>
                  )}
                  {b.status === 'done' && (
                    <button onClick={() => handleViewLogs(b.id)} style={{ background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>📋 Логи</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewLogsId && (
          <div style={{ background: 'white', borderRadius: 12, marginTop: 24, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h4>Логи рассылки #{viewLogsId}</h4>
            <button onClick={() => { setViewLogsId(null); setLogs([]); }} style={{ marginBottom: 16, background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 14px', cursor: 'pointer' }}>Закрыть</button>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {logs.map((l, idx) => (
                <li key={idx} style={{ marginBottom: 6, fontSize: 14 }}>
                  chat_id: {l.chat_id}, успех: {l.success ? '✅' : '❌'} {l.error ? `(${l.error})` : ''}, {l.sent_at}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Broadcasts;